import React, { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import Logo from "./Logo";
import { useAuth } from "../contexts/AuthContext";
import { List, X, House, Wrench, ChatCircle, SignOut, User } from "@phosphor-icons/react";
import { isLaunched } from "../lib/launch";

export default function Header() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const launched = isLaunched();
  // Show auth CTAs / gated nav links only after launch, OR when a user is already signed in.
  const showAuthCTAs = launched || !!user;

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const navLinkCls = ({ isActive }) =>
    `px-3 py-2 rounded-md text-sm font-medium transition-colors ${
      isActive
        ? "text-[var(--brand-green)] bg-[rgba(17,82,59,0.06)]"
        : "text-[var(--text-muted)] hover:text-[var(--brand-green)] hover:bg-[rgba(17,82,59,0.04)]"
    }`;

  return (
    <header className="glass-header sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-5 lg:px-8 h-16 flex items-center justify-between">
        <Link to="/" data-testid="header-logo-link" className="flex items-center">
          <Logo size={80} />
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          <NavLink to="/" end className={navLinkCls} data-testid="nav-home">Home</NavLink>
          {launched && <NavLink to="/find-trades" className={navLinkCls} data-testid="nav-find-trades">Find Trades</NavLink>}
          <NavLink to="/pricing" className={navLinkCls} data-testid="nav-pricing">Pricing</NavLink>
          <NavLink to="/how-it-works" className={navLinkCls} data-testid="nav-how">How it works</NavLink>
          <NavLink to="/faq" className={navLinkCls} data-testid="nav-faq">FAQ</NavLink>
          <NavLink to="/contact" className={navLinkCls} data-testid="nav-contact">Contact</NavLink>
        </nav>

        <div className="hidden md:flex items-center gap-2">
          {!user && showAuthCTAs && (
            <>
              <Link to="/login" className="btn-ghost" data-testid="header-login-btn">Log in</Link>
              <Link to="/register" className="btn-primary" data-testid="header-signup-btn">Get started</Link>
            </>
          )}
          {!user && !showAuthCTAs && (
            <Link to="/" className="btn-primary" data-testid="header-waitlist-btn">Join waitlist</Link>
          )}
          {user && (
            <>
              <Link
                to={user.role === "tradesperson" ? "/dashboard/trader" : "/dashboard/customer"}
                className="btn-ghost"
                data-testid="header-dashboard-btn"
              >
                <House size={18} weight="duotone" /> Dashboard
              </Link>
              <Link to="/messages" className="btn-ghost" data-testid="header-messages-btn">
                <ChatCircle size={18} weight="duotone" /> Messages
              </Link>
              {user.role === "admin" && (
                <Link to="/admin" className="btn-ghost" data-testid="header-admin-btn">
                  Admin
                </Link>
              )}
              <button onClick={handleLogout} className="btn-primary" data-testid="header-logout-btn">
                <SignOut size={18} /> Log out
              </button>
            </>
          )}
        </div>

        <button
          className="md:hidden p-2 rounded-md text-[var(--brand-green)]"
          onClick={() => setOpen((o) => !o)}
          data-testid="header-menu-toggle"
          aria-label="Toggle menu"
        >
          {open ? <X size={24} /> : <List size={24} />}
        </button>
      </div>

      {open && (
        <div className="md:hidden border-t border-[var(--border-stone)] bg-white">
          <div className="px-5 py-3 flex flex-col gap-2">
            <NavLink to="/" end className={navLinkCls} onClick={() => setOpen(false)} data-testid="m-nav-home">Home</NavLink>
            {launched && <NavLink to="/find-trades" className={navLinkCls} onClick={() => setOpen(false)} data-testid="m-nav-find">Find Trades</NavLink>}
            <NavLink to="/pricing" className={navLinkCls} onClick={() => setOpen(false)} data-testid="m-nav-pricing">Pricing</NavLink>
            <NavLink to="/how-it-works" className={navLinkCls} onClick={() => setOpen(false)} data-testid="m-nav-how">How it works</NavLink>
            <NavLink to="/faq" className={navLinkCls} onClick={() => setOpen(false)} data-testid="m-nav-faq">FAQ</NavLink>
            <NavLink to="/contact" className={navLinkCls} onClick={() => setOpen(false)} data-testid="m-nav-contact">Contact</NavLink>
            {!user && showAuthCTAs ? (
              <div className="flex gap-2 mt-2">
                <Link to="/login" className="btn-ghost flex-1 justify-center" onClick={() => setOpen(false)} data-testid="m-login">Log in</Link>
                <Link to="/register" className="btn-primary flex-1 justify-center" onClick={() => setOpen(false)} data-testid="m-signup">Get started</Link>
              </div>
            ) : !user ? (
              <Link to="/" className="btn-primary justify-center mt-2" onClick={() => setOpen(false)} data-testid="m-waitlist">Join waitlist</Link>
            ) : (
              <div className="flex flex-col gap-2 mt-2">
                <Link
                  to={user.role === "tradesperson" ? "/dashboard/trader" : "/dashboard/customer"}
                  className="btn-ghost justify-center"
                  onClick={() => setOpen(false)}
                  data-testid="m-dashboard"
                >Dashboard</Link>
                <Link to="/messages" className="btn-ghost justify-center" onClick={() => setOpen(false)} data-testid="m-messages">Messages</Link>
                {user.role === "admin" && (
                  <Link to="/admin" className="btn-ghost justify-center" onClick={() => setOpen(false)} data-testid="m-admin">Admin</Link>
                )}
                <button onClick={() => { setOpen(false); handleLogout(); }} className="btn-primary justify-center" data-testid="m-logout">Log out</button>
              </div>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
